import type { ChangeEvent } from 'react';
import { forwardRef } from 'react';
import styled from '@emotion/styled';
import type { BasicInputProps } from '../basic-input';
import { BasicInput } from '../basic-input';

type NumberInputProps = Omit<BasicInputProps, 'type'> & {
  prefix?: string;
  suffix?: string;
};

const InputContainer = styled.div({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  width: 'fit-content',
});

const PrefixSuffix = styled.span({
  position: 'absolute',
  pointerEvents: 'none',
  zIndex: 1,
});

const Prefix = styled(PrefixSuffix)({
  left: '16px',
  top: '56%',
});

const Suffix = styled(PrefixSuffix)({
  right: '10px',
  top: '58%'
});

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ onInput, prefix, suffix, style, ...rest }, ref) => {
    const restrictToNumbers = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      if (!isNaN(Number(value))) {
        onInput?.(event);
      } else {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    if (prefix || suffix) {
      return (
        <InputContainer>
          {prefix ? <Prefix>{prefix}</Prefix> : null}
          <BasicInput
            onInput={restrictToNumbers}
            ref={ref}
            style={{
              paddingLeft: prefix ? '32px' : '16px',
              paddingRight: suffix ? '18px' : '16px',
              ...style,
            }}
            type="number"
            {...rest}
          />
          {suffix ? <Suffix>{suffix}</Suffix> : null}
        </InputContainer>
      );
    }

    return (
      <BasicInput
        onInput={restrictToNumbers}
        ref={ref}
        type="number"
        {...rest}
      />
    );
  }
);

NumberInput.displayName = 'NumberInput';
